import { useEffect, useState } from "react";
import api, { fmt, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Users } from "lucide-react";

const emptyCuad = { codigo: "", descripcion: "", integrantes: [] };
const emptyInt = { categoria: "", codigo_insumo: "", cantidad: 1, factor: 1, costo_unitario: 0 };

export default function CuadrillasView() {
  const { user } = useAuth();
  const canWrite = user?.role !== "consulta";
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/cuadrillas");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    const d = dialog.data;
    try {
      if (dialog.mode === "edit") await api.put(`/cuadrillas/${d.id}`, d);
      else await api.post("/cuadrillas", d);
      toast.success("Cuadrilla guardada");
      setDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar ${c.codigo}?`)) return;
    await api.delete(`/cuadrillas/${c.id}`);
    toast.success("Cuadrilla eliminada");
    load();
  };

  const liveTotal = (dialog?.data.integrantes || []).reduce(
    (s, i) => s + (parseFloat(i.cantidad) || 0) * (parseFloat(i.factor) || 0) * (parseFloat(i.costo_unitario) || 0),
    0
  );

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="h-12 shrink-0 border-b border-border flex items-center px-3 gap-2">
        <Users className="h-4 w-4 text-primary" />
        <h2 className="font-head font-bold">Catálogo de Cuadrillas</h2>
        <span className="font-mono-num text-xs text-muted-foreground">{items.length}</span>
        <div className="flex-1" />
        {canWrite && (
          <Button size="sm" className="h-8 rounded-sm gap-1" onClick={() => setDialog({ mode: "create", data: { ...emptyCuad, integrantes: [] } })} data-testid="new-cuadrilla-btn">
            <Plus className="h-4 w-4" /> Nueva cuadrilla
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-auto p-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3 content-start">
        {items.map((c) => (
          <div key={c.id} className="border border-border rounded-sm" data-testid={`cuadrilla-card-${c.codigo}`}>
            <div className="flex items-center justify-between px-3 h-9 border-b border-border bg-muted/30">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-mono-num text-primary text-xs">{c.codigo}</span>
                <span className="text-sm truncate">{c.descripcion}</span>
              </div>
              {canWrite && (
                <div className="flex gap-0.5 shrink-0">
                  <Button variant="ghost" size="icon" className="h-6 w-6 rounded-sm" onClick={() => setDialog({ mode: "edit", data: { ...c, integrantes: c.integrantes.map((i) => ({ ...i })) } })} data-testid={`edit-cuadrilla-${c.codigo}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-6 w-6 rounded-sm text-destructive" onClick={() => remove(c)} data-testid={`delete-cuadrilla-${c.codigo}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </div>
            <table className="qc-table">
              <thead>
                <tr>
                  <th>Integrante</th>
                  <th className="text-right" style={{ width: 50 }}>Cant.</th>
                  <th className="text-right" style={{ width: 50 }}>Factor</th>
                  <th className="text-right" style={{ width: 90 }}>Costo</th>
                </tr>
              </thead>
              <tbody>
                {c.integrantes.map((i, idx) => (
                  <tr key={idx}>
                    <td>{i.categoria}</td>
                    <td className="text-right font-mono-num">{fmt(i.cantidad, 2)}</td>
                    <td className="text-right font-mono-num">{fmt(i.factor, 2)}</td>
                    <td className="text-right font-mono-num">{fmt(i.costo_unitario, 2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center justify-between px-3 h-9 border-t border-border">
              <span className="text-xs text-muted-foreground uppercase tracking-wide">Costo cuadrilla / jornada</span>
              <span className="font-mono-num text-primary font-medium">$ {fmt(c.costo_total, 2)}</span>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-muted-foreground text-sm py-10 col-span-full text-center">Sin cuadrillas.</div>
        )}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-head">{dialog?.mode === "edit" ? "Editar cuadrilla" : "Nueva cuadrilla"}</DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Código</Label>
                  <Input className="mt-1 font-mono-num" value={dialog.data.codigo} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, codigo: e.target.value } }))} data-testid="cuadrilla-codigo" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Descripción</Label>
                  <Input className="mt-1" value={dialog.data.descripcion} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, descripcion: e.target.value } }))} data-testid="cuadrilla-descripcion" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">Integrantes</Label>
                  <Button variant="outline" size="sm" className="h-7 rounded-sm gap-1" onClick={() => setDialog((d) => ({ ...d, data: { ...d.data, integrantes: [...d.data.integrantes, { ...emptyInt }] } }))} data-testid="add-integrante-btn">
                    <Plus className="h-3.5 w-3.5" /> Agregar
                  </Button>
                </div>
                <table className="qc-table border border-border">
                  <thead>
                    <tr>
                      <th>Categoría</th>
                      <th style={{ width: 70 }}>Cant.</th>
                      <th style={{ width: 70 }}>Factor</th>
                      <th style={{ width: 110 }}>Costo unit.</th>
                      <th style={{ width: 32 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {dialog.data.integrantes.map((it, idx) => (
                      <tr key={idx}>
                        {["categoria", "cantidad", "factor", "costo_unitario"].map((field) => (
                          <td key={field}>
                            <input
                              type={field === "categoria" ? "text" : "number"}
                              step="0.01"
                              value={it[field]}
                              onChange={(e) => {
                                const v = field === "categoria" ? e.target.value : parseFloat(e.target.value) || 0;
                                setDialog((d) => {
                                  const arr = d.data.integrantes.map((x) => ({ ...x }));
                                  arr[idx][field] = v;
                                  return { ...d, data: { ...d.data, integrantes: arr } };
                                });
                              }}
                              className={`w-full bg-transparent outline-none px-1 py-0.5 ${field === "categoria" ? "" : "text-right font-mono-num"}`}
                            />
                          </td>
                        ))}
                        <td className="text-center">
                          <button className="text-destructive" onClick={() => setDialog((d) => ({ ...d, data: { ...d.data, integrantes: d.data.integrantes.filter((_, i) => i !== idx) } }))}>
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end items-center gap-2 pt-1">
                <span className="text-xs text-muted-foreground uppercase">Costo total</span>
                <span className="font-mono-num text-primary font-medium">$ {fmt(liveTotal, 2)}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setDialog(null)}>Cancelar</Button>
            <Button className="rounded-sm" onClick={save} data-testid="cuadrilla-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
